import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import Tabs from './shared/Tabs';
import vaultService from '../services/vaultService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsourcegroupvaulttableService from '../services/vaultsourcegroupvaulttableService';
import './VaultQuery.css';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from './support/ProductSupportTab';

const VaultQuery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useSupportTab('query');   // Query | Support

  const [vault, setVault] = useState(null);
  const [sourceGroups, setSourceGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [queryResults, setQueryResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [error, setError] = useState(null);
  const [customQuery, setCustomQuery] = useState('');
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    fetchVaultDetails();
  }, [id]);

  const fetchVaultDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch vault details
      const vaultResponse = await vaultService.getVaultById(id);
      console.log('Vault details:', vaultResponse);
      setVault(vaultResponse);

      // If vault has a vaultsourceid, fetch associated source groups
      if (vaultResponse.vaultsourceid) {
        console.log('Fetching vault source groups for vaultsourceid:', vaultResponse.vaultsourceid);

        // Get vault source groups for this vault source
        const sourceGroupsResponse = await vaultsourcegroupService.getAllVaultSourceGroups({
          vaultsourceid: vaultResponse.vaultsourceid
        });
        console.log('Vault source groups response:', sourceGroupsResponse);

        const groups = sourceGroupsResponse.vaultsourcegroups || [];
        console.log('Vault source groups:', groups);

        // Fetch table count for each group
        const groupsWithTables = await Promise.all(
          groups.map(async (group) => {
            try {
              const tablesResponse = await vaultsourcegroupvaulttableService.getVaultTablesForGroup(group.id);
              const tables = tablesResponse.vaulttables || [];
              return {
                ...group,
                tableCount: tables.length,
                tables: tables
              };
            } catch (err) {
              console.error(`Failed to fetch tables for group ${group.id}:`, err);
              return {
                ...group,
                tableCount: 0,
                tables: []
              };
            }
          })
        );

        console.log('Source groups with table data:', groupsWithTables);
        setSourceGroups(groupsWithTables);
      } else {
        console.log('No vaultsourceid found for vault');
        setSourceGroups([]);
      }
    } catch (err) {
      console.error('Error loading vault:', err);
      setError(`Failed to load vault: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = async (group) => {
    setSelectedGroup(group);

    // If the group has SQL defined, use that; otherwise generate query from tables
    if (group.sql_source) {
      setCustomQuery(group.sql_source);
    } else if (group.tables && group.tables.length > 0) {
      // Generate a simple query for the first table in the group
      const firstTable = group.tables[0];
      setCustomQuery(`SELECT * FROM ${firstTable.name} LIMIT ${limit}`);
    } else {
      setCustomQuery('');
    }
  };

  const executeQuery = async () => {
    if (!customQuery.trim()) {
      setError('Please enter a query');
      return;
    }

    try {
      setQuerying(true);
      setError(null);

      // TODO: Implement API endpoint to execute Athena query
      // const results = await vaultService.executeQuery(vault.vaultid, customQuery);
      // setQueryResults(results);

      setError('Query execution not yet implemented');
    } catch (err) {
      console.error('Error executing query:', err);
      setError(`Query failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setQuerying(false);
    }
  };

  const handleQuickQuery = (group) => {
    setSelectedGroup(group);
    if (group.sql_source) {
      setCustomQuery(group.sql_source);
    } else if (group.tables && group.tables.length > 0) {
      const firstTable = group.tables[0];
      setCustomQuery(`SELECT * FROM ${firstTable.name} LIMIT ${limit}`);
    }
    executeQuery();
  };

  if (loading) {
    return (
      <div className="vault-query-container">
        <div className="loading-container">
          <div className="loading-spinner">🔄</div>
          <p>Loading vault...</p>
        </div>
      </div>
    );
  }

  if (error && !vault) {
    return (
      <div className="vault-query-container">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchVaultDetails} className="retry-button">Retry</button>
          <button onClick={() => navigate('/vaults')} className="back-button">Back to Vaults</button>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-query-container">
      <div className="vault-query-header">
        <button onClick={() => navigate('/vaults')} className="back-button">
          ← Back to Vaults
        </button>
        <div className="vault-info">
          <div className="vault-title-row">
            <h1>Vault Query: {vault?.name || 'Unnamed Vault'}</h1>
          </div>
          <div className="vault-meta">
            <span className="meta-item">Database: {vault?.database_type || 'N/A'}</span>
            <span className="meta-item">Status: {vault?.status || 'unknown'}</span>
            <span className={`meta-item ${vault?.active === 'Y' ? 'active' : 'inactive'}`}>
              {vault?.active === 'Y' ? '✓ Active' : '✗ Inactive'}
            </span>
          </div>
        </div>
      </div>

      <Tabs tabs={[{ key: 'query', label: 'Query', icon: <Search size={20} /> }, SUPPORT_TAB]} active={tab}
        onChange={setTab} />

      {tab === 'support' ? (
        <ProductSupportTab product="Vault" productId={id} noun="this vault"
          context={{ screen: 'vault-query', vaultId: id, vaultName: vault?.name, database: vault?.database_type, status: vault?.status }} />
      ) : (
      <div className="query-workspace">
        <div className="tables-panel">
          <h3>Source Groups</h3>
          {sourceGroups.length === 0 ? (
            <div className="no-tables">
              <p>No source groups available for this vault</p>
              <p className="hint">Source groups will appear here once configured</p>
            </div>
          ) : (
            <div className="tables-list">
              {sourceGroups.map((group) => (
                <div
                  key={group.id}
                  className={`table-item ${selectedGroup?.id === group.id ? 'selected' : ''}`}
                  onClick={() => handleGroupSelect(group)}
                >
                  <span className="table-icon">📦</span>
                  <div className="group-info">
                    <span className="table-name">{group.name}</span>
                    <span className="group-table-count">{group.tableCount} table{group.tableCount !== 1 ? 's' : ''}</span>
                  </div>
                  <button
                    className="quick-query-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickQuery(group);
                    }}
                    title="Quick query this group"
                  >
                    ▶
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="query-panel">
          <div className="query-editor">
            <div className="query-controls">
              <h3>Query Editor</h3>
              <div className="control-group">
                <label>
                  Limit:
                  <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
                    min="1"
                    max="10000"
                    className="limit-input"
                  />
                </label>
                <button
                  onClick={executeQuery}
                  disabled={querying || !customQuery.trim()}
                  className="execute-button"
                >
                  {querying ? '⏳ Executing...' : '▶ Execute Query'}
                </button>
              </div>
            </div>
            <textarea
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              placeholder="Enter your SQL query here..."
              className="query-textarea"
              rows="8"
            />
          </div>

          {error && (
            <div className="query-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {queryResults && (
            <div className="query-results">
              <div className="results-header">
                <h3>Query Results</h3>
                <span className="results-count">
                  {queryResults.rows?.length || 0} rows
                </span>
              </div>
              <div className="results-table-container">
                {queryResults.rows?.length > 0 ? (
                  <table className="results-table">
                    <thead>
                      <tr>
                        {queryResults.columns?.map((col, idx) => (
                          <th key={idx}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queryResults.rows.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {queryResults.columns?.map((col, colIdx) => (
                            <td key={colIdx}>{row[col] !== null ? String(row[col]) : 'NULL'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-results">No results returned</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default VaultQuery;
